import React, { useEffect, useState } from 'react';
import { X, Loader2, AlertCircle, Lock } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import { obterLead, atualizarLead, Lead } from '../../services/api.ts';

interface EditLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: number | null | undefined;
  contactPhone: string;
  contactName: string;
  clinicId: number;
  onSaved?: (updated: Lead) => void;
}

const SOURCE_OPTIONS = [
  'Facebook',
  'Instagram',
  'Google',
  'Indicação',
  'Fachada',
  'Site',
  'Orgânico',
  'Convênio',
  'Carteira de Pacientes',
  'Radio',
  'TV',
];

const isProtectedSource = (sourceId?: string | null): boolean => {
  if (!sourceId) return false;
  return /^\d{10,}$/.test(sourceId);
};

const formatPhoneDisplay = (raw: string): string => {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  // Remove DDI 55 if present
  const local = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
  if (local.length === 11) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 7)}-${local.slice(7)}`;
  }
  if (local.length === 10) {
    return `(${local.slice(0, 2)}) ${local.slice(2, 6)}-${local.slice(6)}`;
  }
  return raw;
};

const toDatetimeLocal = (raw?: string): string => {
  if (!raw) return '';
  return raw.substring(0, 16).replace(' ', 'T');
};

const EditLeadModal: React.FC<EditLeadModalProps> = ({
  isOpen,
  onClose,
  leadId,
  contactPhone,
  contactName,
  clinicId,
  onSaved,
}) => {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lead, setLead] = useState<Lead | null>(null);
  const [form, setForm] = useState({
    name: '',
    source_id: 'Facebook',
    data_entrada: '',
  });

  useEffect(() => {
    if (!isOpen) return;
    setSaveError(null);
    if (!leadId) {
      setLoading(false);
      setLoadError(null);
      setLead(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const userType = localStorage.getItem('user_type');
        const clientId =
          userType === 'user'
            ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
            : parseInt(localStorage.getItem('client_id') || '0', 10);
        console.log('[EditLeadModal] Carregando lead', { leadId, clientId, clinicId });

        const fetched = await obterLead(clientId, clinicId, leadId);
        if (cancelled) return;

        setLead(fetched);
        setForm({
          name: fetched.name || contactName || '',
          source_id: fetched.source_id || 'Facebook',
          data_entrada: toDatetimeLocal(fetched.data_entrada),
        });
      } catch (err: any) {
        console.error('[EditLeadModal] Erro ao buscar lead:', err);
        if (!cancelled) {
          setLoadError(err?.response?.data?.detail || 'Erro ao carregar dados do lead');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, leadId, clinicId, contactName]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (!lead || !leadId) return;
    if (!form.name.trim()) {
      setSaveError('Nome é obrigatório');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const userType = localStorage.getItem('user_type');
      const clientId =
        userType === 'user'
          ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
          : parseInt(localStorage.getItem('client_id') || '0', 10);
      const payload: Partial<Lead> = { name: form.name.trim() };
      if (form.data_entrada) {
        payload.data_entrada = form.data_entrada.replace('T', ' ') + ':00';
      }
      if (!isProtectedSource(form.source_id)) {
        payload.source_id = form.source_id;
      }

      console.log('[EditLeadModal] Salvando alterações', { leadId, payload });
      const updated = await atualizarLead(clientId, clinicId, leadId, payload as any);
      console.log('[EditLeadModal] Lead atualizado', updated);

      onSaved?.(updated);
      onClose();
    } catch (err: any) {
      console.error('[EditLeadModal] Erro ao salvar:', err);
      setSaveError(err?.response?.data?.detail || err?.message || 'Erro ao salvar lead');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const protectedSrc = isProtectedSource(form.source_id);
  const isMissingLead = !leadId;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      <div
        className={`relative w-full max-w-md rounded-2xl shadow-xl flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200 ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        role="dialog"
        aria-label="Editar lead"
      >
        <div
          className={`px-5 py-4 border-b flex items-center justify-between flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <div>
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Editar lead
            </h2>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Atualize os dados básicos do lead
            </p>
          </div>
          <button
            onClick={onClose}
            title="Fechar (Esc)"
            className={`p-2 rounded-xl transition-colors ${
              isDark
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {isMissingLead ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-3 ${
                  isDark ? 'bg-yellow-900/30' : 'bg-yellow-50'
                }`}
              >
                <AlertCircle className={`w-8 h-8 ${isDark ? 'text-yellow-300' : 'text-yellow-600'}`} />
              </div>
              <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                Este contato ainda não é um lead
              </h3>
              <p className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Para editar dados de lead, primeiro converta este contato em lead pelo CRM.
              </p>
            </div>
          ) : loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin" />
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-3 ${
                  isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-500'
                }`}
              >
                <AlertCircle className="w-8 h-8" />
              </div>
              <p className={`text-sm text-center ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {loadError}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nome
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={`w-full p-3 border rounded-xl text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="Nome completo"
                />
              </div>

              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Telefone
                </label>
                <input
                  type="text"
                  value={formatPhoneDisplay(contactPhone)}
                  disabled
                  className={`w-full p-3 border rounded-xl text-sm cursor-not-allowed ${
                    isDark
                      ? 'bg-gray-700/50 border-gray-700 text-gray-400'
                      : 'bg-gray-100 border-gray-200 text-gray-500'
                  }`}
                />
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  O telefone não pode ser alterado.
                </p>
              </div>

              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Origem (Mídia)
                </label>
                {protectedSrc ? (
                  <>
                    <div
                      className={`w-full p-3 border rounded-xl text-sm flex items-center gap-2 ${
                        isDark
                          ? 'bg-gray-700/50 border-gray-700 text-gray-300'
                          : 'bg-gray-100 border-gray-200 text-gray-600'
                      }`}
                    >
                      <Lock className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{form.source_id}</span>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                      ID de anúncio do Meta Ads — protegido contra edição.
                    </p>
                  </>
                ) : (
                  <select
                    value={form.source_id}
                    onChange={(e) => setForm({ ...form, source_id: e.target.value })}
                    className={`w-full p-3 border rounded-xl text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${
                      isDark
                        ? 'bg-gray-700 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {SOURCE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                    {/* Caso o source_id atual seja um valor custom não listado, preserva ele */}
                    {form.source_id && !SOURCE_OPTIONS.includes(form.source_id) && (
                      <option value={form.source_id}>{form.source_id}</option>
                    )}
                  </select>
                )}
              </div>

              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  Data de entrada
                </label>
                <input
                  type="datetime-local"
                  value={form.data_entrada}
                  onChange={(e) => setForm({ ...form, data_entrada: e.target.value })}
                  className={`w-full p-3 border rounded-xl text-sm transition-colors focus:ring-2 focus:ring-sminy-primary focus:border-sminy-primary ${
                    isDark
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                />
              </div>

              {saveError && (
                <div
                  className={`flex items-start gap-2 p-3 rounded-xl text-sm ${
                    isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-600'
                  }`}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{saveError}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div
          className={`px-5 py-4 border-t flex items-center justify-end gap-2 flex-shrink-0 ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <button
            onClick={onClose}
            disabled={saving}
            className={`rounded-xl border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 ${
              isDark
                ? 'border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Cancelar
          </button>
          {!isMissingLead && !loadError && (
            <button
              onClick={handleSave}
              disabled={loading || saving || !form.name.trim()}
              className="rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm font-medium text-white transition-colors flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Salvar
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditLeadModal;
